/**
 * Navigation Tool Generator — reads app manifest pages and generates
 * a single navigate_to tool with page routes as enum values.
 */

import type { CogoApp } from "@cogo/types"
import type { WebMCPTool } from "../core/tool-registry"
import type { GeneratorRuntime } from "./form-tools"

// ── Generator ──────────────────────────────────────────────

export function generateNavigationTools(
  app: CogoApp,
  narratives?: Record<string, { effect: string }>,
): WebMCPTool[] {
  const pages = app.pages ?? []
  if (pages.length === 0) return []

  const routes = pages.map((p) => p.route)
  const routeDescriptions = pages
    .map((p) => `  "${p.route}" — ${p.title ?? p.name ?? p.id}`)
    .join("\n")

  const tools: WebMCPTool[] = [
    {
      name: "navigate_to",
      description: narratives?.["navigate_to"]?.effect
        ?? `Navigate to a page in "${app.name}". Available routes:\n${routeDescriptions}`,
      inputSchema: {
        type: "object",
        properties: {
          route: {
            type: "string",
            enum: routes,
            description: "Target page route",
          },
          params: {
            type: "object",
            description: "Route parameters (e.g., { id: \"123\" } for /edit/:id)",
          },
        },
        required: ["route"],
      },
      handler: createNavigateHandler(),
      metadata: {
        readOnly: false,
        tags: ["navigation"],
      },
    },
  ]

  return tools
}

function createNavigateHandler(): (
  params: Record<string, unknown>,
) => (runtime: GeneratorRuntime) => Promise<unknown> {
  return (params: Record<string, unknown>) => {
    return async (runtime: GeneratorRuntime) => {
      let route = params.route as string

      // Replace route params like /edit/:id with actual values
      if (params.params && typeof params.params === "object") {
        const routeParams = params.params as Record<string, string>
        for (const [key, value] of Object.entries(routeParams)) {
          route = route.replace(`:${key}`, String(value))
        }
      }

      return runtime.actionEngine.executeFlow("__navigate__", { route })
    }
  }
}
